import { useState, useEffect } from 'react';

interface Beat {
  id: string;
  title: string;
  price: number;
  licenseType: string;
  isFree: boolean;
  sold: boolean;
  bpm: number;
  key: string;
  genre: string[];
  audioUrl: string;
  downloadUrl?: string;
  waveformImage: string;
}

interface NowPlayingBarProps {
  currentBeat: Beat | null;
  isPlaying: boolean;
  currentTime: number;
  duration: number;
  volume: number;
  onPlayPause: () => void;
  onPrev: () => void;
  onNext: () => void;
  onSeek: (time: number) => void;
  onVolumeChange: (vol: number) => void;
}

export default function NowPlayingBar({
  currentBeat,
  isPlaying,
  currentTime,
  duration,
  volume,
  onPlayPause,
  onPrev,
  onNext,
  onSeek,
  onVolumeChange,
}: NowPlayingBarProps) {
  const [visible, setVisible] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (currentBeat) {
      setDismissed(false);
      setVisible(true);
    }
  }, [currentBeat?.id]);

  const formatTime = (t: number) => {
    if (!t || isNaN(t)) return '0:00';
    const m = Math.floor(t / 60);
    const s = Math.floor(t % 60);
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  const progressPercent = duration > 0 ? (currentTime / duration) * 100 : 0;

  if (!currentBeat || !visible || dismissed) return null;

  const beatStatusLabel = currentBeat.isFree
    ? 'FREE'
    : currentBeat.sold
    ? 'SOLD'
    : `$${currentBeat.price}`;

  const beatStatusClass = currentBeat.isFree
    ? 'text-green-400'
    : currentBeat.sold
    ? 'text-red-400 font-black tracking-widest'
    : 'text-gray-300';

  return (
    <div className="now-playing-bar fixed bottom-0 left-0 right-0 z-50">
      {/* Progress bar at very top of bar */}
      <div className="relative h-1 bg-white/10 cursor-pointer group">
        <div
          className="h-full bg-gradient-to-r from-red-500 to-red-700 transition-all duration-150"
          style={{ width: `${progressPercent}%` }}
        />
        <input
          type="range"
          min="0"
          max={duration || 0}
          value={currentTime}
          onChange={e => onSeek(parseFloat(e.target.value))}
          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
        />
      </div>

      {/* Mobile bar */}
      <div className="md:hidden bg-gray-950/95 backdrop-blur-xl border-t border-white/8 px-4 py-2.5">
        <div className="flex items-center gap-3">
          {/* Thumbnail + info */}
          <div className="flex items-center gap-2.5 flex-1 min-w-0">
            <div className="relative w-10 h-10 rounded-lg overflow-hidden flex-shrink-0">
              <img
                src={currentBeat.waveformImage}
                alt={currentBeat.title}
                className="w-full h-full object-cover object-top"
              />
              {isPlaying && (
                <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                  <div className="flex items-end gap-0.5 h-3">
                    <span className="w-0.5 bg-red-500 rounded-full animate-[equalize_0.8s_ease-in-out_infinite]" style={{ height: '60%' }}></span>
                    <span className="w-0.5 bg-red-500 rounded-full animate-[equalize_0.8s_ease-in-out_0.2s_infinite]" style={{ height: '100%' }}></span>
                    <span className="w-0.5 bg-red-500 rounded-full animate-[equalize_0.8s_ease-in-out_0.4s_infinite]" style={{ height: '40%' }}></span>
                  </div>
                </div>
              )}
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-white text-xs font-semibold truncate leading-tight">{currentBeat.title}</p>
              <p className="text-gray-500 text-xs truncate mt-0.5">{currentBeat.genre[0]} · {currentBeat.bpm} BPM</p>
            </div>
          </div>

          {/* Controls */}
          <div className="flex items-center gap-1 flex-shrink-0">
            <button
              onClick={onPrev}
              className="w-8 h-8 flex items-center justify-center text-gray-500 hover:text-white transition-colors cursor-pointer"
            >
              <i className="ri-skip-back-fill text-base"></i>
            </button>
            <button
              onClick={onPlayPause}
              className="w-10 h-10 flex items-center justify-center bg-white text-gray-900 rounded-full hover:scale-105 transition-transform cursor-pointer shadow-md flex-shrink-0"
            >
              {isPlaying
                ? <i className="ri-pause-fill text-base"></i>
                : <i className="ri-play-fill text-base ml-0.5"></i>
              }
            </button>
            <button
              onClick={onNext}
              className="w-8 h-8 flex items-center justify-center text-gray-500 hover:text-white transition-colors cursor-pointer"
            >
              <i className="ri-skip-forward-fill text-base"></i>
            </button>
          </div>

          {/* Right actions */}
          <div className="flex items-center gap-1.5 flex-shrink-0">
            <span className={`text-xs font-bold ${beatStatusClass}`}>{beatStatusLabel}</span>
            <button
              onClick={() => setDismissed(true)}
              className="w-8 h-8 flex items-center justify-center text-gray-600 hover:text-white transition-colors cursor-pointer rounded-full hover:bg-white/10"
            >
              <i className="ri-close-line text-base"></i>
            </button>
          </div>
        </div>
      </div>

      {/* Desktop bar */}
      <div className="hidden md:block bg-gray-950/95 backdrop-blur-xl border-t border-white/8 px-6 py-0">
        <div className="max-w-7xl mx-auto h-16 flex items-center gap-6">

          {/* Left — Beat info */}
          <div className="flex items-center gap-3 w-56 flex-shrink-0 min-w-0">
            <div className="relative w-10 h-10 rounded-lg overflow-hidden flex-shrink-0">
              <img
                src={currentBeat.waveformImage}
                alt={currentBeat.title}
                className="w-full h-full object-cover object-top"
              />
              {isPlaying && (
                <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                  <div className="flex items-end gap-0.5 h-4">
                    <span className="w-0.5 bg-red-500 rounded-full animate-[equalize_0.8s_ease-in-out_infinite]" style={{ height: '60%' }}></span>
                    <span className="w-0.5 bg-red-500 rounded-full animate-[equalize_0.8s_ease-in-out_0.2s_infinite]" style={{ height: '100%' }}></span>
                    <span className="w-0.5 bg-red-500 rounded-full animate-[equalize_0.8s_ease-in-out_0.4s_infinite]" style={{ height: '40%' }}></span>
                    <span className="w-0.5 bg-red-500 rounded-full animate-[equalize_0.8s_ease-in-out_0.1s_infinite]" style={{ height: '80%' }}></span>
                  </div>
                </div>
              )}
            </div>
            <div className="min-w-0">
              <p className="text-white text-sm font-semibold truncate leading-tight">{currentBeat.title}</p>
              <p className="text-gray-500 text-xs truncate mt-0.5">
                {currentBeat.genre.slice(0, 2).join(' · ')}
                <span className="ml-2 text-gray-600">{currentBeat.bpm} BPM</span>
              </p>
            </div>
          </div>

          {/* Center — Controls */}
          <div className="flex-1 flex items-center justify-center gap-5">
            <button
              onClick={onPrev}
              className="w-8 h-8 flex items-center justify-center text-gray-500 hover:text-white transition-colors cursor-pointer"
            >
              <i className="ri-skip-back-fill text-lg"></i>
            </button>

            <button
              onClick={onPlayPause}
              className="w-10 h-10 flex items-center justify-center bg-white text-gray-900 rounded-full hover:scale-105 transition-transform cursor-pointer shadow-lg"
            >
              {isPlaying
                ? <i className="ri-pause-fill text-lg"></i>
                : <i className="ri-play-fill text-lg ml-0.5"></i>
              }
            </button>

            <button
              onClick={onNext}
              className="w-8 h-8 flex items-center justify-center text-gray-500 hover:text-white transition-colors cursor-pointer"
            >
              <i className="ri-skip-forward-fill text-lg"></i>
            </button>

            <span className="text-xs text-gray-600 tabular-nums ml-2">
              {formatTime(currentTime)} / {formatTime(duration)}
            </span>
          </div>

          {/* Right — Volume + status */}
          <div className="flex items-center gap-4 w-56 flex-shrink-0 justify-end">
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 flex items-center justify-center">
                <i className={`text-xs ${volume === 0 ? 'ri-volume-mute-line text-gray-600' : 'ri-volume-up-line text-gray-500'}`}></i>
              </div>
              <div className="relative w-20 h-1 bg-white/10 rounded-full">
                <div
                  className="absolute left-0 top-0 h-full bg-gray-500 rounded-full"
                  style={{ width: `${volume * 100}%` }}
                />
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.01"
                  value={volume}
                  onChange={e => onVolumeChange(parseFloat(e.target.value))}
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                />
              </div>
            </div>

            {/* Beat status label */}
            <span className={`text-sm font-bold px-3 py-1.5 rounded-lg ${
              currentBeat.isFree
                ? 'bg-green-500/20 text-green-400'
                : currentBeat.sold
                  ? 'bg-red-500/20 text-red-400 tracking-widest uppercase text-xs'
                  : 'bg-white/10 text-gray-300'
            }`}>
              {beatStatusLabel}
            </span>

            <button
              onClick={() => setDismissed(true)}
              className="w-7 h-7 flex items-center justify-center text-gray-600 hover:text-white transition-colors cursor-pointer rounded-full hover:bg-white/10"
            >
              <i className="ri-close-line text-base"></i>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
